import { useState } from "react";
import { PlaystorePublishError, publishToPlaystore } from "@limrun/play-auth";
import { CheckCircle2, Loader } from "lucide-react";
import { Link } from "react-router-dom";

import { useGooglePlaySession } from "@/client/googlePlay";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const PACKAGE_NAME_STORAGE_KEY = "limrun.playstore.packageName";

type PlaystorePublishDialogProps = {
	assetName: string;
	registryApiUrl?: string;
	registryToken?: string;
	organizationId?: string;
	disabled?: boolean;
};

export function PlaystorePublishDialog({
	assetName,
	registryApiUrl,
	registryToken,
	organizationId,
	disabled,
}: PlaystorePublishDialogProps) {
	const google = useGooglePlaySession();
	const [packageName, setPackageName] = useState(() => localStorage.getItem(PACKAGE_NAME_STORAGE_KEY) ?? "");

	const [open, setOpen] = useState(false);
	const [publishing, setPublishing] = useState(false);
	const [versionCode, setVersionCode] = useState<number>();
	const [error, setError] = useState<string>();
	const [errorCode, setErrorCode] = useState<string>();

	const canPublish = google.isSignedIn && packageName.trim() !== "" && !publishing;

	const handleOpenChange = (next: boolean) => {
		// A publish holds a slot server-side; closing mid-flight would orphan
		// it and leave the busy guard swallowing the next Publish.
		if (!next && publishing) {
			return;
		}
		setOpen(next);
		if (!next) {
			setVersionCode(undefined);
			setError(undefined);
			setErrorCode(undefined);
		}
	};

	const handlePublish = async () => {
		const accessToken = google.getAccessToken();
		if (!accessToken || !registryApiUrl || publishing) {
			return;
		}
		localStorage.setItem(PACKAGE_NAME_STORAGE_KEY, packageName.trim());
		setPublishing(true);
		setError(undefined);
		setErrorCode(undefined);
		setVersionCode(undefined);
		try {
			const result = await publishToPlaystore({
				registryApiUrl,
				token: registryToken,
				organizationId,
				accessToken,
				assetName,
				packageName: packageName.trim(),
			});
			setVersionCode(result.versionCode);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : "Play publish failed");
			setErrorCode(caught instanceof PlaystorePublishError ? caught.code : undefined);
		} finally {
			setPublishing(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>
				<Button className="w-full" disabled={disabled || !registryApiUrl}>
					Publish to Play Store
				</Button>
			</DialogTrigger>
			<DialogContent className="max-w-lg">
				<DialogHeader>
					<DialogTitle>Publish to Google Play</DialogTitle>
					<DialogDescription>
						Publish <span className="break-all font-medium">{assetName}</span> to the internal track using the Google
						account you signed in with on the Secrets page.
					</DialogDescription>
				</DialogHeader>

				{error && (
					<Alert variant="destructive">
						<AlertTitle>{errorCode ? "Publish failed" : "Something went wrong"}</AlertTitle>
						<AlertDescription>{friendlyError(errorCode, error)}</AlertDescription>
					</Alert>
				)}

				{versionCode !== undefined ? (
					<div className="space-y-4">
						<div className="flex items-center gap-2 rounded-md border border-green-600/30 bg-green-600/10 p-3 text-sm">
							<CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" />
							<span>
								Version code <span className="font-medium">{versionCode}</span> is on the internal track. Roll it
								out to testers in{" "}
								<a
									className="underline"
									href="https://play.google.com/console"
									target="_blank"
									rel="noreferrer"
								>
									Play Console
								</a>
								.
							</span>
						</div>
						<Button variant="outline" className="w-full" onClick={() => setVersionCode(undefined)}>
							Publish another version
						</Button>
					</div>
				) : (
					<div className="space-y-4">
						{google.isSignedIn ? (
							<div className="flex items-center gap-2 rounded-md border p-3 text-sm text-muted-foreground">
								<CheckCircle2 className="h-4 w-4 text-green-600" />
								Signed in with Google
							</div>
						) : (
							<div className="rounded-md border p-3 text-sm text-muted-foreground">
								Not signed in with Google.{" "}
								<Link className="underline" to="/secrets">
									Sign in on the Secrets page
								</Link>{" "}
								with the account that has release access to the app in Play Console, then come back here.
							</div>
						)}

						<label className="space-y-2">
							<Label>Package name</Label>
							<Input
								value={packageName}
								onChange={(event) => setPackageName(event.currentTarget.value)}
								placeholder="com.example.app"
							/>
							<p className="text-xs text-muted-foreground">
								The application ID of the app in Play Console. The app listing must already exist.
							</p>
						</label>

						<Button className="w-full" onClick={() => void handlePublish()} disabled={!canPublish}>
							{publishing ? (
								<>
									<Loader className="mr-2 h-4 w-4 animate-spin" />
									Publishing...
								</>
							) : (
								"Publish"
							)}
						</Button>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}

function friendlyError(code: string | undefined, fallback: string): string {
	switch (code) {
		case "versionCodeExists":
			return "This version code is already on Google Play. Bump versionCode, rebuild and publish again.";
		case "uploadKeyMismatch":
			return "The bundle is signed with a different key than this app's Play upload key.";
		case "listingNotFound":
			return "App not found on Google Play. Check the package name and that your account has access to it in Play Console.";
		case "permissionDenied":
			return "Your Google account can't publish this app. Check its permissions in Play Console, or sign in again if it's been a while.";
		default:
			return fallback;
	}
}
